import { TopCounterLayout, formatCoinCount, parseCoinCount } from "../../common_ui_elements/coin_counter_pill.js";
import { Vector2 } from "../../../common/vector2.js";
import { AvatarUtils } from "../../../common/avatar_utils.js";
import { AvatarImageBinder } from "../../../common/avatar_image_binder.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { heartCountText, heartTimerText } from "../../../utils/heart_timer.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UITextElement } from "../../common_ui_elements/ui_text_element.js";

export interface AdventureRewardHudBadge
{
  icon: UIImage;
  target: Vector2;
}

export interface AdventureRewardHudRefs
{
  row: UIElementHolder;
  avatar_holder: UIElement;
  avatar_target: Vector2;
  heart: AdventureRewardHudBadge;
  coin: AdventureRewardHudBadge;
  coin_count: UITextElement;
  heart_timer: UITextElement;
  heart_count: UITextElement | null;
  heart_plus: UIImage | null;
}

export interface AdventureRewardHudOptions
{
  /** Draws the menu top bar's "+" on the badges, for a copy laid over the menu. */
  plus?: boolean;
}

interface BuiltBadge
{
  badge: AdventureRewardHudBadge;
  count: UITextElement;
  icon_count: UITextElement | null;
  plus: UIImage | null;
}

export class AdventureRewardHud
{
  static build( ctx: CanvasRenderingContext2D, parent: UIElement, screen_w: number, stats: GameStatistics = StatisticsStorage.load(),
    options: AdventureRewardHudOptions = {} ): AdventureRewardHudRefs
  {
    const row = new UIElementHolder( ctx, 0, 0, screen_w, LayoutScaler.heightFromDesign( 222 ), false );
    parent.addChild( row );

    const now_ms = Date.now();
    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    const avatar_holder = this.buildAvatar( ctx, row );
    const heart = this.buildBadge(
      ctx,
      row,
      TopCounterLayout.heartX(),
      hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart,
      "heart",
      stats,
      now_ms,
      options.plus === true
    );
    const coin = this.buildBadge( ctx, row, TopCounterLayout.coinX(), ImgID.IconCoin, "coin", stats, now_ms, options.plus === true );

    return {
      row,
      avatar_holder,
      avatar_target: this.absoluteCenter( avatar_holder ),
      heart: heart.badge,
      coin: coin.badge,
      coin_count: coin.count,
      heart_timer: heart.count,
      heart_count: heart.icon_count,
      heart_plus: heart.plus,
    };
  }

  static animateCoins( hud: AdventureRewardHudRefs, to: number, start_time: number, duration_ms: number ): void
  {
    const shown = parseCoinCount( hud.coin_count.getText() );
    const from = Number.isFinite( shown ) ? shown : to;
    if ( from === to )
    {
      hud.coin_count.setText( formatCoinCount( to ) );
      return;
    }

    hud.row.addTransform( new TransformValue(
      from,
      to,
      start_time,
      duration_ms,
      value => hud.coin_count.setText( formatCoinCount( Math.round( value ) ) ),
      { easing: Easing.EaseOutQuad }
    ) );
  }

  static refreshHearts( hud: AdventureRewardHudRefs, stats: GameStatistics, now_ms: number ): void
  {
    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    hud.heart_timer.setText( heartTimerText( stats, now_ms ) );
    hud.heart_count?.setText( hearts_infinite ? "" : heartCountText( stats, now_ms ) );
    hud.heart.icon.setImageId( hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart );
    hud.heart_plus?.setOpacityImmediate( AdventureRewardHud.heartPlusVisible( stats, now_ms ) ? 1 : 0 );
  }

  /** Same rule as the menu's top bar: no "+" on the heart while hearts are full or infinite. */
  private static heartPlusVisible( stats: GameStatistics, now_ms: number ): boolean
  {
    return stats.infinite_hearts_until <= now_ms && heartTimerText( stats, now_ms ) !== "MAX";
  }

  private static buildAvatar( ctx: CanvasRenderingContext2D, row: UIElement ): UIElement
  {
    const circle_x = LayoutScaler.xFromDesign( 30 );
    const circle_y = LayoutScaler.yFromDesign( 62 );
    const circle_size = LayoutScaler.widthFromDesign( 220 );

    const holder = new UIElementHolder( ctx, circle_x, circle_y, circle_size, circle_size, false );
    row.addChild( holder );

    const bg_circle = new RoundedRectangle(
      ctx, 0, 0, circle_size, circle_size, circle_size / 2,
      { type: "linear-gradient", from: "#F9F4C9", to: "#B5A084", direction: "vertical" }
    );
    holder.addChild( bg_circle );

    const avatar_id = Number.parseInt( localStorage.getItem( "player_avatar_id" ) ?? "8", 10 );
    const avatar_size = LayoutScaler.widthFromDesign( 200 );
    const avatar_inset = ( circle_size - avatar_size ) / 2;

    const avatar = new UIImage(
      ctx, avatar_inset, avatar_inset, avatar_size, avatar_size,
      AvatarUtils.getAvatarImgId( Number.isFinite( avatar_id ) ? avatar_id : 8 ),
      {}, false, avatar_size / 2
    );
    holder.addChild( avatar );
    new AvatarImageBinder( avatar ).applyFromStorage( 8 );

    return holder;
  }

  private static buildBadge( ctx: CanvasRenderingContext2D, row: UIElement, x: number, img_id: ImgID, type: "coin" | "heart", stats: GameStatistics, now_ms: number, with_plus: boolean ): BuiltBadge
  {
    const m = TopCounterLayout.metrics();
    const icon_w = m.icon_w;
    const icon_h = m.icon_h;

    const bg = new UINineSliceElement( ctx, x, TopCounterLayout.y(), m.bg_w, m.bg_h, ImgID.UI_MenuBlockBg, "menu_top_bg" );
    row.addChild( bg );

    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    const count = type === "coin" ? formatCoinCount( stats.coins ) : heartTimerText( stats, now_ms );

    const count_text = TopCounterLayout.createCountText( ctx, m, count );
    bg.addChild( count_text );

    const icon = new UIImage( ctx, m.icon_x, m.icon_y, icon_w, icon_h, img_id );
    bg.addChild( icon );

    let icon_count: UITextElement | null = null;
    if ( type === "heart" )
    {
      icon_count = new UITextElement( ctx, 0, LayoutScaler.yFromDesign( -7 ), icon_w, icon_h, {
        text: hearts_infinite ? "" : heartCountText( stats, now_ms ),
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      } );
      icon.addChild( icon_count );
    }

    if ( !with_plus )
      return { badge: { icon, target: this.absoluteCenter( icon ) }, count: count_text, icon_count, plus: null };

    // The "+" the menu's top bar has, at the same spot: without it the copy laid over
    // the menu made the "+" vanish and pop back when the copy faded out.
    const plus = new UIImage( ctx, m.plus_x, m.plus_y, m.plus_w, m.plus_h, ImgID.IconPlus );
    icon.addChild( plus );
    if ( type === "heart" && !this.heartPlusVisible( stats, now_ms ) )
      plus.setOpacityImmediate( 0 );

    return { badge: { icon, target: this.absoluteCenter( icon ) }, count: count_text, icon_count, plus };
  }

  private static absoluteCenter( el: UIElement ): Vector2
  {
    const pos = el.getAbsolutePosition();
    const rect = el.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }
}
